import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Form,
  Input,
  Button,
  message,
  Card,
  Typography,
  Checkbox,
  Space,
  Divider,
  Radio,
} from 'antd';
import { Link, Navigate } from 'react-router';
import { SIGN_UP } from '../../graphql/mutations/mutations';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuth } from '../../providers/AuthProvider';
import { RegisterFormValues } from '../../types/auth';
import { AuthWithSocial } from './AuthWithSocial';

const { Title } = Typography;

const EmailSent: React.FC<{ email: string }> = ({ email }) => {
  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Check your email</Title>
      <p>We&apos;ve sent a confirmation email to {email}</p>
      <Link to="/login">
        <Button type="primary" style={{ width: '100%' }}>
          Go to login
        </Button>
      </Link>
    </Card>
  );
};

const RegisterForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>();
  const { loggedIn, setAuthUser } = useAuth();
  const selectedRole = Form.useWatch('role', form);

  const [signUp] = useMutation(SIGN_UP, {
    onCompleted: data => {
      if (data?.signUp?.email) {
        message.success('Registration successful! Please check your email.');
        setRegisteredEmail(form.getFieldValue('email'));
      }
      setLoading(false);
    },
    onError: error => {
      const errors = error.graphQLErrors;
      errors.forEach(err => {
        if (err.message.includes('email')) {
          form.setFields([
            {
              name: 'email',
              errors: ['Email has already been taken'],
            },
          ]);
        }
      });
      message.error('Registration failed');
      setLoading(false);
    },
  });

  if (loggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = (values: RegisterFormValues) => {
    setLoading(true);
    signUp({
      variables: {
        email: values.email,
        password: values.password,
        name: values.name,
        roles: [values.role],
      },
    });
  };

  if (registeredEmail) {
    return <EmailSent email={registeredEmail} />;
  }

  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Create your account</Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          terms: false,
          role: 'USER',
        }}>
        <Form.Item
          name="name"
          label="First Name"
          rules={[{ required: true, message: 'Please enter your name' }]}>
          <Input placeholder="How you like to be addressed" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}>
          <Input placeholder="your@email.com" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: 'Please enter your password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /[0-9]/,
              message: 'Password must contain at least one number',
            },
            {
              pattern: /[a-z]/,
              message: 'Password must contain at least one lowercase letter',
            },
            {
              pattern: /[A-Z]/,
              message: 'Password must contain at least one uppercase letter',
            },
          ]}>
          <Input.Password placeholder="Your password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}>
          <Input.Password placeholder="Confirm password" />
        </Form.Item>

        <Form.Item
          name="role"
          label="Account Type"
          rules={[{ required: true, message: 'Please select account type' }]}>
          <Radio.Group>
            <Radio.Button value="USER">Homeowner</Radio.Button>
            <Radio.Button value="CONTRACTOR">Contractor</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="terms"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Please accept the terms and conditions')),
            },
          ]}>
          <Checkbox>
            I agree to{' '}
            <a
              href="https://homewip.com/terms-conditions/"
              target="_blank"
              rel="noopener noreferrer">
              Terms
            </a>{' '}
            and{' '}
            <a href="https://homewip.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginBottom: 'unset' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: '100%', marginBottom: '10px' }}>
              Create account
            </Button>
            <div>
              Already have an account? <Link to="/login">Log in</Link>
            </div>
            {selectedRole == 'USER' && (
              <>
                <Divider />
                <AuthWithSocial
                  onComplete={setAuthUser}
                  onLoading={setLoading}
                  successMessage="Registration successful."
                  errorMessage="Registration failed"
                  type="register"
                  loading={loading}
                />
              </>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export const Register: React.FC = () => {
  return (
    <AuthLayout>
      <RegisterForm />
    </AuthLayout>
  );
};
